/**
 * Insights — sales-follow-up lists computed from users + activity_logs:
 *  1. Most active this week (INSIGHTS.activeWindowDays)
 *  2. Hot leads — same product viewed ≥ INSIGHTS.hotLeadViewCount times
 *  3. Most wishlisted — products dealers saved, straight off the user docs
 *  4. Churn risk — approved users inactive ≥ INSIGHTS.churnRiskDays days
 * Each row carries enough context (name, company, mobile) to make a call.
 */
import { useMemo } from 'react';
import { INSIGHTS } from '@mitang/shared';
import { useUsers, useActivitySince } from '../hooks/useFirestore';
import { useProductsByIds } from '../hooks/useProductsByIds';
import { mostActiveUsers, hotLeads, churnRisk, wishlistedProducts } from '../lib/analytics';
import { daysAgo } from '../lib/time';
import { formatDate, timeAgo } from '../lib/format';
import { Card, SectionHeader, Badge } from '../components/ui';
import { EmptyState, ErrorState, LoadingState } from '../components/States';

export function InsightsPage(): JSX.Element {
  const users = useUsers();
  const weekActivity = useActivitySince(useMemo(() => daysAgo(INSIGHTS.activeWindowDays), []));

  const active = useMemo(
    () => mostActiveUsers(users.data, weekActivity.data),
    [users.data, weekActivity.data],
  );
  const leads = useMemo(
    () => hotLeads(users.data, weekActivity.data),
    [users.data, weekActivity.data],
  );
  const churn = useMemo(() => churnRisk(users.data), [users.data]);
  // Saves come off the user docs already loaded here, so this card is free.
  const wishlisted = useMemo(
    () => wishlistedProducts(users.data).slice(0, INSIGHTS.topWishlisted),
    [users.data],
  );
  const wishlistProducts = useProductsByIds(
    useMemo(() => wishlisted.map((w) => w.productId), [wishlisted]),
  );

  const loading = users.loading || weekActivity.loading;
  const error = users.error ?? weekActivity.error;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Insights"
        subtitle={`Sales follow-up lists. Windows: active ${INSIGHTS.activeWindowDays}d · hot lead ≥${INSIGHTS.hotLeadViewCount} views · churn ≥${INSIGHTS.churnRiskDays}d idle.`}
      />

      {error ? (
        <ErrorState message={error} />
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          {/* Most active */}
          <InsightCard
            title="Most active this week"
            hint={`Top engagement over the last ${INSIGHTS.activeWindowDays} days`}
            badge={<Badge tone="brand">{active.length}</Badge>}
            loading={loading}
            empty={active.length === 0}
            emptyTitle="No activity this week"
          >
            <ul className="divide-y divide-slate-100">
              {active.map((row) => (
                <li key={row.user.id} className="px-4 py-3">
                  <ContactLine
                    name={row.user.name}
                    company={row.user.companyName}
                    mobile={row.user.mobile}
                    email={row.user.email}
                  />
                  <div className="mt-1 flex items-center gap-3 text-xs text-slate-500">
                    <span className="font-medium text-brand-600">{row.events} events</span>
                    <span>{row.sessions} sessions</span>
                    <span>last {timeAgo(row.lastActiveAt)}</span>
                  </div>
                </li>
              ))}
            </ul>
          </InsightCard>

          {/* Hot leads */}
          <InsightCard
            title="Hot leads"
            hint={`Viewed the same product ≥ ${INSIGHTS.hotLeadViewCount} times`}
            badge={<Badge tone="danger">{leads.length}</Badge>}
            loading={loading}
            empty={leads.length === 0}
            emptyTitle="No hot leads yet"
            emptyHint="Repeated product views surface here as strong buying signals."
          >
            <ul className="divide-y divide-slate-100">
              {leads.map((lead) => (
                <li key={`${lead.user.id}|${lead.productId}`} className="px-4 py-3">
                  <ContactLine
                    name={lead.user.name}
                    company={lead.user.companyName}
                    mobile={lead.user.mobile}
                    email={lead.user.email}
                  />
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                    <Badge tone="warn">{lead.viewCount}× views</Badge>
                    <span className="font-semibold text-[11.5px] text-slate-700">
                      {lead.modelNumber ?? lead.productId}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </InsightCard>

          {/* Most wishlisted */}
          <InsightCard
            title="Most wishlisted"
            hint="Products dealers saved for later — a stated buying intent"
            badge={<Badge tone="brand">{wishlisted.length}</Badge>}
            loading={loading}
            empty={wishlisted.length === 0}
            emptyTitle="Nothing saved yet"
            emptyHint="Dealers save products with the heart in the app."
          >
            <ul className="divide-y divide-slate-100">
              {wishlisted.map((row) => (
                <li key={row.productId} className="px-4 py-3">
                  <div className="flex items-baseline justify-between gap-2">
                    <p className="min-w-0 truncate text-sm font-medium text-slate-800">
                      {wishlistProducts.get(row.productId)?.modelNumber ?? row.productId}
                    </p>
                    <Badge tone="warn">
                      {row.savers.length} {row.savers.length === 1 ? 'save' : 'saves'}
                    </Badge>
                  </div>
                  <p className="mt-1 truncate text-xs text-slate-500">
                    {row.savers
                      .slice(0, 3)
                      .map(({ user }) => user.name || user.email)
                      .join(', ')}
                    {row.savers.length > 3 ? ` +${row.savers.length - 3} more` : ''}
                  </p>
                </li>
              ))}
            </ul>
          </InsightCard>

          {/* Churn risk */}
          <InsightCard
            title="Churn risk"
            hint={`Approved dealers idle ≥ ${INSIGHTS.churnRiskDays} days`}
            badge={<Badge tone="warn">{churn.length}</Badge>}
            loading={loading}
            empty={churn.length === 0}
            emptyTitle="No churn risk"
            emptyHint="All approved dealers are active within the window."
          >
            <ul className="divide-y divide-slate-100">
              {churn.map((row) => (
                <li key={row.user.id} className="px-4 py-3">
                  <ContactLine
                    name={row.user.name}
                    company={row.user.companyName}
                    mobile={row.user.mobile}
                    email={row.user.email}
                  />
                  <div className="mt-1 flex items-center gap-3 text-xs text-slate-500">
                    <span className="font-medium text-amber-600">
                      {Number.isFinite(row.daysInactive) ? `${row.daysInactive}d idle` : 'never active'}
                    </span>
                    <span>last {formatDate(row.user.lastActiveAt)}</span>
                  </div>
                </li>
              ))}
            </ul>
          </InsightCard>
        </div>
      )}
    </div>
  );
}

function InsightCard({
  title,
  hint,
  badge,
  loading,
  empty,
  emptyTitle,
  emptyHint,
  children,
}: {
  title: string;
  hint: string;
  badge: JSX.Element;
  loading: boolean;
  empty: boolean;
  emptyTitle: string;
  emptyHint?: string;
  children: JSX.Element;
}): JSX.Element {
  return (
    <Card className="flex flex-col">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          <p className="text-[11px] text-slate-500">{hint}</p>
        </div>
        {badge}
      </div>
      <div className="max-h-[28rem] flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4">
            <LoadingState label="Computing…" />
          </div>
        ) : empty ? (
          <div className="p-4">
            <EmptyState title={emptyTitle} hint={emptyHint} />
          </div>
        ) : (
          children
        )}
      </div>
    </Card>
  );
}

function ContactLine({
  name,
  company,
  mobile,
  email,
}: {
  name: string;
  company: string;
  mobile: string;
  email: string;
}): JSX.Element {
  // Email is the guaranteed identity (signup no longer collects a name; older
  // Skip-signups have nothing but the email).
  const primary = name || email || '—';
  const secondary = [name ? email : null, company].filter(Boolean).join(' · ');
  return (
    <div className="flex items-baseline justify-between gap-2">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-slate-800">{primary}</p>
        <p className="truncate text-xs text-slate-500">{secondary || 'No profile yet'}</p>
      </div>
      {mobile ? (
        <a
          href={`tel:${mobile}`}
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 font-mono text-[13px] text-brand-600 hover:underline"
        >
          {mobile}
        </a>
      ) : null}
    </div>
  );
}
